import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple, FloppyDisk, Bell, Sparkle, Pause, Play } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import AuditLogPanel from "@/components/AuditLogPanel";

const METRICS = [
  { value: "m1a_pct", label: "M1a Perdita %" },
  { value: "m1b_m3_km_gg", label: "M1b m³/km/gg" },
  { value: "perdite_m3_gg", label: "Perdite m³/gg" },
  { value: "immesso_m3_gg", label: "Immesso m³/gg" },
  { value: "fatturato_m3_gg", label: "Fatturato m³/gg" },
];
const OPERATORS = [">", ">=", "<", "<="];

function RuleForm({ initial, onSubmit, onCancel }) {
  const [f, setF] = useState(initial || {
    name: "", metric: "m1a_pct", operator: ">", value: 30,
    notify_email: false, email_to: "", enabled: true,
  });
  const setV = (k, v) => setF({ ...f, [k]: v });

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      ...f,
      value: parseFloat(f.value),
      email_to: f.notify_email ? (f.email_to || null) : null,
    };
    onSubmit(payload);
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-400">Nome regola</Label>
        <Input required value={f.name} onChange={(e) => setV("name", e.target.value)} placeholder="Es. M1a critico" className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" data-testid="rule-name-input" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-400">Metrica</Label>
          <Select value={f.metric} onValueChange={(v) => setV("metric", v)}>
            <SelectTrigger className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono text-xs" data-testid="rule-metric-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-800">
              {METRICS.map((m) => <SelectItem key={m.value} value={m.value} className="font-mono text-xs">{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-400">Operatore</Label>
          <Select value={f.operator} onValueChange={(v) => setV("operator", v)}>
            <SelectTrigger className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono text-xs" data-testid="rule-operator-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-800">
              {OPERATORS.map((o) => <SelectItem key={o} value={o} className="font-mono text-xs">{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-400">Soglia</Label>
          <Input type="number" step="0.01" required value={f.value} onChange={(e) => setV("value", e.target.value)} className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" data-testid="rule-value-input" />
        </div>
      </div>
      <div className="flex items-center gap-3 pt-2">
        <Switch checked={f.notify_email} onCheckedChange={(v) => setV("notify_email", v)} data-testid="rule-email-switch" />
        <Label className="text-xs text-slate-300">Invia email quando la regola scatta</Label>
      </div>
      {f.notify_email && (
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-400">Email destinatario</Label>
          <Input type="email" value={f.email_to || ""} onChange={(e) => setV("email_to", e.target.value)} placeholder="ops@aquasave.it" className="mt-2 bg-[#0a0f1c] border-slate-800 font-mono" data-testid="rule-email-input" />
        </div>
      )}
      <div className="flex items-center gap-3">
        <Switch checked={f.enabled} onCheckedChange={(v) => setV("enabled", v)} data-testid="rule-enabled-switch" />
        <Label className="text-xs text-slate-300">Regola attiva</Label>
      </div>
      <DialogFooter>
        <Button variant="outline" type="button" onClick={onCancel} className="border-slate-700">Annulla</Button>
        <Button type="submit" className="bg-[#3EB6F0] text-[#070B14] hover:bg-[#2CA4DE] gap-2" data-testid="rule-save-button">
          <FloppyDisk weight="bold" size={14} /> Salva regola
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function AlertRulesPanel() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState({ type: null, item: null });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/v1/alert-rules");
      setRules(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Errore caricamento regole")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      if (edit.item) await api.put(`/v1/admin/alert-rules/${edit.item.id}`, form);
      else await api.post("/v1/admin/alert-rules", form);
      toast.success("Regola salvata");
      setEdit({ type: null, item: null }); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Errore salvataggio")); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare la regola?")) return;
    try { await api.delete(`/v1/admin/alert-rules/${id}`); toast.success("Eliminata"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Errore eliminazione")); }
  };

  const installPresets = async () => {
    try {
      const { data } = await api.post("/v1/admin/alert-rules/presets");
      const c = (data.created || []).length;
      const s = (data.skipped || []).length;
      toast.success(`Preset ARERA: ${c} nuove, ${s} già presenti`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Errore preset")); }
  };

  const snooze = async (id, minutes) => {
    try {
      await api.put(`/v1/admin/alert-rules/${id}/snooze?minutes=${minutes}`);
      toast.success(minutes === 0 ? "Snooze rimosso" : `Regola in pausa per ${minutes} minuti`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Errore snooze")); }
  };

  if (loading) return <div className="text-center py-8 text-xs text-slate-500 font-mono">Caricamento...</div>;

  const metricLabel = (v) => METRICS.find((m) => m.value === v)?.label || v;

  return (
    <div className="space-y-6">
    <div className="bg-[#111827] border border-slate-800 rounded-lg overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Bell size={16} weight="duotone" className="text-[#F59E0B]" />
          <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Regole custom · valutate ad ogni /metrics · email auto-triggered</div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={installPresets} data-testid="install-presets-button"
            className="border-[#F59E0B]/40 text-[#F59E0B] hover:bg-amber-500/10 gap-2">
            <Sparkle weight="fill" size={14} /> Installa preset ARERA
          </Button>
          <Dialog open={edit.type === "new"} onOpenChange={(o) => setEdit(o ? { type: "new", item: null } : { type: null, item: null })}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-[#3EB6F0] text-[#070B14] hover:bg-[#2CA4DE] gap-2" data-testid="add-rule-button">
                <Plus weight="bold" size={14} /> Nuova regola
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#111827] border-slate-800 text-slate-100">
              <DialogHeader><DialogTitle className="font-heading">Nuova regola allarme</DialogTitle></DialogHeader>
              <RuleForm onSubmit={save} onCancel={() => setEdit({ type: null, item: null })} />
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <table className="w-full text-xs">
        <thead className="text-slate-500 uppercase tracking-widest">
          <tr className="border-b border-slate-800">
            <th className="text-left px-4 py-2 font-mono">Nome</th>
            <th className="text-left px-4 py-2 font-mono">Metrica</th>
            <th className="text-center px-2 py-2 font-mono">Regola</th>
            <th className="text-center px-2 py-2 font-mono">Email</th>
            <th className="text-center px-2 py-2 font-mono">Stato</th>
            <th className="text-center px-3 py-2 font-mono">Azioni</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {rules.length === 0 && (
            <tr><td colSpan={6} className="text-center py-6 text-slate-500">Nessuna regola definita. Crea la prima.</td></tr>
          )}
          {rules.map((r) => {
            const isSnoozed = r.snooze_until && new Date(r.snooze_until) > new Date();
            return (
            <tr key={r.id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`rule-row-${r.id}`}>
              <td className="px-4 py-2 text-slate-200 font-semibold">{r.name}</td>
              <td className="px-4 py-2 text-slate-400">{metricLabel(r.metric)}</td>
              <td className="px-2 py-2 text-center text-[#3EB6F0]">{r.operator} {r.value}</td>
              <td className="px-2 py-2 text-center">
                {r.notify_email ? <span className="text-[#10B981]">{r.email_to || "✓"}</span> : <span className="text-slate-600">—</span>}
              </td>
              <td className="px-2 py-2 text-center">
                {!r.enabled ? <span className="text-slate-600">disattivata</span>
                  : isSnoozed ? <span className="text-[#F59E0B] text-[10px]" data-testid={`rule-snoozed-${r.id}`}>⏸ fino {new Date(r.snooze_until).toLocaleTimeString("it-IT")}</span>
                  : <span className="text-[#10B981]">● attiva</span>}
              </td>
              <td className="px-3 py-2">
                <div className="flex items-center justify-center gap-3">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className={`${isSnoozed ? "text-[#F59E0B]" : "text-slate-500"} hover:text-[#F59E0B]`} data-testid={`snooze-btn-${r.id}`} title="Snooze regola">
                        {isSnoozed ? <Play size={14} weight="bold" /> : <Pause size={14} weight="bold" />}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 bg-[#111827] border-slate-800 p-2" data-testid={`snooze-popover-${r.id}`}>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 px-2 py-1">Pausa regola per</div>
                      {[15, 60, 240, 1440].map((m) => (
                        <button key={m} onClick={() => snooze(r.id, m)} data-testid={`snooze-${r.id}-${m}`}
                          className="w-full text-left px-2 py-1.5 text-xs font-mono text-slate-300 hover:bg-slate-800 rounded">
                          {m === 15 ? "15 min" : m === 60 ? "1 ora" : m === 240 ? "4 ore" : "24 ore"}
                        </button>
                      ))}
                      {isSnoozed && (
                        <button onClick={() => snooze(r.id, 0)} data-testid={`snooze-${r.id}-0`}
                          className="w-full text-left px-2 py-1.5 text-xs font-mono text-[#10B981] hover:bg-slate-800 rounded border-t border-slate-800 mt-1">
                          ▶ Rimuovi snooze
                        </button>
                      )}
                    </PopoverContent>
                  </Popover>
                  <Dialog open={edit.type === "edit" && edit.item?.id === r.id} onOpenChange={(o) => setEdit(o ? { type: "edit", item: r } : { type: null, item: null })}>
                    <DialogTrigger asChild>
                      <button className="text-slate-500 hover:text-[#3EB6F0]" data-testid={`edit-rule-${r.id}`}><PencilSimple size={14} weight="bold" /></button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#111827] border-slate-800 text-slate-100">
                      <DialogHeader><DialogTitle className="font-heading">Modifica regola</DialogTitle></DialogHeader>
                      <RuleForm initial={r} onSubmit={save} onCancel={() => setEdit({ type: null, item: null })} />
                    </DialogContent>
                  </Dialog>
                  <button className="text-slate-500 hover:text-[#EF4444]" onClick={() => del(r.id)} data-testid={`delete-rule-${r.id}`}>
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              </td>
            </tr>
          );})}
        </tbody>
      </table>
    </div>
    <AuditLogPanel rules={rules} />
    </div>
  );
}
